import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { AppLayout } from "../shared/layout";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { useToast } from "@/components/ui/use-toast";
import {
  getPaymentHistoryGroups,
  getPaymentHistoryByGroupId,
  downloadGroupInvoicePdfs,
  markPaymentAsNotPaid,
  markPaymentAsReturn,
} from "@/data/requests/invoice-requests";
import { SearchView } from "@/components";
import ListView from "@/components/list-view";
import { countryOptions } from "@/utils/country-options";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatTRY } from "@/utils/currency";

const STATUS_OPTIONS = [
  { value: "all", label: "Tümü" },
  { value: "paid", label: "Ödendi" },
  { value: "pending", label: "Beklemede" },
  { value: "notPaid", label: "Ödenmedi" },
  { value: "return", label: "İade" },
];

export function PaymentHistoryListScreen() {
  const { toast } = useToast();
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [isGroupDetailsOpen, setIsGroupDetailsOpen] = useState(false);
  const [isReturnDialogOpen, setIsReturnDialogOpen] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [returnReason, setReturnReason] = useState("");
  const [pageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [searchParams, setSearchParams] = useState({
    country: "",
  });

  const { data: paymentHistoryGroups, isLoading: isLoadingPaymentHistory } =
    useQuery({
      queryKey: ["paymentHistoryGroups", page, pageSize, searchParams],
      queryFn: () =>
        getPaymentHistoryGroups({
          page,
          pageSize,
          countryFilter: searchParams.country,
        }),
      onError: (error) => {
        toast({
          title: "Ödeme geçmişi yüklenirken hata oluştu",
          description:
            error.response?.data?.message || "Beklenmeyen bir hata oluştu",
          variant: "destructive",
        });
      },
    });

  const {
    data: groupDetails,
    isLoading: isLoadingGroupDetails,
    refetch: refetchGroupDetails,
  } = useQuery({
    queryKey: ["paymentHistoryGroup", selectedGroupId],
    queryFn: () => getPaymentHistoryByGroupId(selectedGroupId),
    enabled: !!selectedGroupId && isGroupDetailsOpen,
    onError: (error) => {
      toast({
        title: "Grup detayları yüklenirken hata oluştu",
        description:
          error.response?.data?.message || "Beklenmeyen bir hata oluştu",
        variant: "destructive",
      });
    },
  });

  const handleViewGroupDetails = (groupId) => {
    setSelectedGroupId(groupId);
    setIsGroupDetailsOpen(true);
  };

  const handleDownloadGroupPdfs = (groupId) => {
    downloadGroupInvoicePdfs(groupId)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `invoices-${groupId}.zip`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast({
          title: "İndirme başarılı",
          description: "Fatura PDF'leri ZIP dosyası olarak indirildi.",
          variant: "default",
        });
      })
      .catch((error) => {
        toast({
          title: "PDF'ler indirilirken hata oluştu",
          description:
            error.response?.data?.message || "Beklenmeyen bir hata oluştu",
          variant: "destructive",
        });
        console.error("Error downloading PDFs:", error);
      });
  };

  const handleMarkAsNotPaid = (paymentHistoryId) => {
    markPaymentAsNotPaid(paymentHistoryId)
      .then(() => {
        toast({
          title: "Başarılı",
          description: "Ödeme, ödenmedi olarak işaretlendi",
          variant: "default",
        });
        // Refresh the group details
        if (selectedGroupId) {
          refetchGroupDetails();
        }
      })
      .catch((error) => {
        toast({
          title: "Hata",
          description:
            error.response?.data?.message || "Beklenmeyen bir hata oluştu",
          variant: "destructive",
        });
      });
  };

  const handleReturnClick = (paymentHistoryId) => {
    setSelectedPaymentId(paymentHistoryId);
    setIsReturnDialogOpen(true);
  };

  const handleReturnSubmit = () => {
    if (!returnReason.trim()) {
      toast({
        title: "Hata",
        description: "Lütfen bir iade sebebi girin",
        variant: "destructive",
      });
      return;
    }

    markPaymentAsReturn(selectedPaymentId, returnReason)
      .then(() => {
        toast({
          title: "Başarılı",
          description: "Ödeme, iade olarak işaretlendi",
          variant: "default",
        });
        setIsReturnDialogOpen(false);
        setReturnReason("");
        if (selectedGroupId) {
          refetchGroupDetails();
        }
      })
      .catch((error) => {
        toast({
          title: "Hata",
          description:
            error.response?.data?.message || "Beklenmeyen bir hata oluştu",
          variant: "destructive",
        });
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return format(date, "dd.MM.yyyy HH:mm");
    } catch (error) {
      return dateString;
    }
  };

  const handleSearch = (item) => {
    const newSearchParams = { ...searchParams, [item.key]: item.value };
    setSearchParams(newSearchParams);
    setPage(1);
  };

  const pageSettings = {
    title: "Ödeme Geçmişi",
  };

  const columns = [
    {
      key: "date",
      label: "Tarih",
      accessor: (item) => formatDate(item.createdAt),
    },
    {
      key: "paymentDate",
      label: "Ödeme Tarihi",
      accessor: (item) => item.paymentDate || "-",
    },
    {
      key: "recordCount",
      label: "Kayıt",
      accessor: (item) => item.recordCount,
    },
    {
      key: "totalAmount",
      label: "Toplam Tutar",
      accessor: (item) => formatTRY(item.totalAmount),
    },
    {
      key: "currencies",
      label: "Para Birimleri",
      accessor: (item) => item.currencies.join(", ") || "-",
    },
    {
      key: "country",
      label: "Ülke",
      accessor: (item) => item.country || "-",
    },
    {
      key: "actions",
      label: "İşlemler",
      render: (row) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewGroupDetails(row.groupId)}
          >
            Detayları Gör
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={() => handleDownloadGroupPdfs(row.groupId)}
          >
            PDF'leri İndir
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppLayout
      fullscreen={true}
      title={pageSettings.title}
      loading={isLoadingPaymentHistory}
      style={{ padding: "0 10px" }}
    >
      <SearchView
        searchParams={searchParams}
        handleSearch={handleSearch}
        statusOptions={STATUS_OPTIONS}
        countryOptions={countryOptions}
        resetDefault={() => {
          setSearchParams({
            date: "",
            status: "",
            country: "",
          });
          setPage(1);
        }}
      />

      <ListView
        isLoading={isLoadingPaymentHistory}
        data={paymentHistoryGroups?.data}
        columns={columns}
        totalItems={paymentHistoryGroups?.totalItems}
        enablePagination={true}
        itemsPerPage={pageSize}
        currentPage={page}
        onPageChange={setPage}
        renderGridItem={(item) => (
          <div className="p-4 border rounded-lg shadow-sm hover:shadow-md transition-shadow">
            <div className="space-y-2">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm font-medium">
                    Date: {formatDate(item.createdAt)}
                  </p>
                  <p className="text-sm">
                    Payment Date: {item.paymentDate || "-"}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">
                    Records: {item.recordCount}
                  </p>
                  <p className="text-sm">
                    Tutar: {formatTRY(item.totalAmount)}
                  </p>
                </div>
              </div>
              <p className="text-sm">
                Currencies: {item.currencies.join(", ") || "-"}
              </p>
              <div className="flex gap-2 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleViewGroupDetails(item.groupId)}
                >
                  Detayları Gör
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  className="flex-1"
                  onClick={() => handleDownloadGroupPdfs(item.groupId)}
                >
                  PDF'leri İndir
                </Button>
              </div>
            </div>
          </div>
        )}
      />

      {/* Group Details Dialog */}
      <Dialog open={isGroupDetailsOpen} onOpenChange={setIsGroupDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ödeme Grubu Detayları</DialogTitle>
            <DialogDescription>
              Ödeme grubu detayları: {selectedGroupId}
            </DialogDescription>
          </DialogHeader>

          {isLoadingGroupDetails ? (
            <div className="flex justify-center p-4">
              Grup detayları yükleniyor...
            </div>
          ) : groupDetails?.data ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-md">
                <div>
                  <p className="text-sm font-medium">Grup No:</p>
                  <p className="text-sm">{groupDetails.data.groupId}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Ödeme Tarihi:</p>
                  <p className="text-sm">
                    {groupDetails.data.paymentDate || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Toplam Tutar:</p>
                  <p className="text-sm">
                    {formatTRY(groupDetails.data.totalAmount)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Para Birimleri:</p>
                  <p className="text-sm">
                    {groupDetails.data.currencies.join(", ") || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Kayıt Sayısı:</p>
                  <p className="text-sm">{groupDetails.data.recordCount}</p>
                </div>
                <div>
                  <p className="text-sm font-medium">Oluşturulma:</p>
                  <p className="text-sm">
                    {formatDate(groupDetails.data.batchCreatedAt)}
                  </p>
                </div>
              </div>

              <Button
                className="w-full"
                onClick={() =>
                  handleDownloadGroupPdfs(groupDetails.data.groupId)
                }
              >
                Tüm PDF'leri İndir
              </Button>

              <Table>
                <TableCaption>Bu gruptaki ödeme kayıtları</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fatura No</TableHead>
                    <TableHead>Müşteri</TableHead>
                    <TableHead>Tutar</TableHead>
                    <TableHead>Para Birimi</TableHead>
                    <TableHead>Banka Adı</TableHead>
                    <TableHead>Alıcı</TableHead>
                    <TableHead>İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupDetails.data.paymentHistories.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {payment.commissionDetail?.invoiceId || "-"}
                      </TableCell>
                      <TableCell>
                        {payment.commissionDetail?.commission?.Client?.name ||
                          "-"}
                      </TableCell>
                      <TableCell>{formatTRY(payment.amount)}</TableCell>
                      <TableCell>{payment.currency || "-"}</TableCell>
                      <TableCell>{payment.bankName || "-"}</TableCell>
                      <TableCell>{payment.recipientName || "-"}</TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleMarkAsNotPaid(payment.id)}
                          >
                            Ödenmedi
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReturnClick(payment.id)}
                          >
                            İade
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center p-4">Bu grup için veri bulunamadı</div>
          )}
        </DialogContent>
      </Dialog>

      {/* Return Reason Dialog */}
      <Dialog open={isReturnDialogOpen} onOpenChange={setIsReturnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ödemeyi İade Et</DialogTitle>
            <DialogDescription>
              Lütfen bu ödemeyi iade etme sebebini belirtin.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="returnReason">İade Sebebi</Label>
              <Input
                id="returnReason"
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                placeholder="İade sebebini girin..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsReturnDialogOpen(false)}
            >
              İptal
            </Button>
            <Button onClick={handleReturnSubmit}>Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
